import { useCallback, useEffect, useState } from "react";
import { ServicesProvider } from "./data/services";
import { NavProvider, useNav, type Route } from "./nav/router";
import { BottomTabBar, Screen, TabRail } from "./nav/Shell";
import { FroshBotPill } from "./components/FroshBotPill";
import { EmptyState } from "./components/states";
import { CourseDetail, ProfessorScreen, StudyTab } from "./screens/Study";
import { CalendarScreen, HomeTab, PostDetail, SearchScreen } from "./screens/Home";
import { ConnectTab, MatchDetail } from "./screens/Connect";
import {
  EditProfile,
  HoroscopeScreen,
  MarketplaceScreen,
  ProfileTab,
  PublicProfile,
  RewardsHistoryScreen,
  SettingsScreen,
} from "./screens/Profile";
import { FroshBotScreen } from "./screens/FroshBot";
import { AppGate } from "./components/AppGate";
import { OnboardingFlow } from "./onboarding/OnboardingFlow";
import { armPayoffKickoff } from "./froshbot";
import { isHosted, startParam } from "./platform/telegram";
import {
  claimWebProfile,
  fetchProfile,
  fetchWaitlist,
  isMarkedOnboarded,
  joinWaitlist,
  markOnboarded,
  resetLocalState,
  type OnboardingProfile,
  type WaitlistEntry,
} from "./api/profile";
import "./components/components.css";

export default function App() {
  return (
    <ServicesProvider>
      <NavProvider>
        <Root />
      </NavProvider>
    </ServicesProvider>
  );
}

type Entry = "loading" | "onboarding" | "app" | "bridge" | "learn";

/**
 * Entry routing — the only place that decides between onboarding and the
 * surfaces. Completed users route straight to the surfaces and never see the
 * flow again (a CloudStorage flag short-circuits even the profile fetch);
 * everyone else with a real Telegram identity onboards first. In a plain
 * browser the surfaces open as before, with `?onboarding` as the dev preview.
 */
async function decideEntry(): Promise<{ entry: Entry; profile: OnboardingProfile | null }> {
  if (!isHosted) {
    // Browser front door: the bare URL onboards (real saves under the
    // anonymous web identity), finished visitors get the Telegram bridge.
    // `?tab=` keeps the demo surfaces reachable; `?onboarding=<step>` stays
    // the per-screen dev preview.
    //
    // /learn paths skip everything: a shared item URL must render instantly
    // for anyone — no onboarding, no identity, no gate (Distribution §1).
    if (window.location.pathname.startsWith("/learn")) {
      return { entry: "app", profile: null };
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has("onboarding")) return { entry: "onboarding", profile: await fetchProfile() };
    if (params.has("tab")) return { entry: "app", profile: null };
    const profile = await fetchProfile();
    if (profile?.onboardingStep === "complete") {
      // A returning visitor who already claimed their waitlist rank has seen
      // the bridge's receipt; bare "/" goes straight where its button goes —
      // the Learn feed. Not-yet-waitlisted completers still get the bridge.
      if (await fetchWaitlist()) return { entry: "learn", profile };
      return { entry: "bridge", profile };
    }
    return { entry: "onboarding", profile };
  }

  // Telegram-side claim: a web-onboarded profile named in the deep link is
  // absorbed into this real identity, then normal routing continues.
  const start = startParam();
  if (start?.startsWith("claim_")) {
    const claimed = await claimWebProfile(start.slice("claim_".length));
    if (claimed) {
      await markOnboarded();
      return { entry: "app", profile: null };
    }
  }

  // Test hook: t.me/<bot>/<app>?startapp=reset clears this device's completion
  // flag + local queues and re-runs the flow (pair with a server-side wipe —
  // see the wipe-telegram-user skill). Harmless if a stranger finds it: their
  // own flow simply runs again against their own profile.
  if (startParam() === "reset") {
    await resetLocalState();
    return { entry: "onboarding", profile: await fetchProfile() };
  }

  if (await isMarkedOnboarded()) return { entry: "app", profile: null };

  const profile = await fetchProfile();
  if (profile?.onboardingStep === "complete") {
    await markOnboarded();
    return { entry: "app", profile };
  }
  if (profile) return { entry: "onboarding", profile };

  // Profile unreachable. The deep link still gets the flow (offline entry —
  // answers park locally); an organic open fails open to the surfaces.
  return startParam() === "onboarding"
    ? { entry: "onboarding", profile: null }
    : { entry: "app", profile: null };
}

/** The hidden kickoff turn behind the payoff (O9): Froshbot's first message
 * must visibly use what was just entered. The profile is also threaded
 * server-side; the inline facts make the moment immune to a racing save. */
function kickoffText(answers: { name: string; goals: string }): string {
  const facts = [
    answers.name ? `They go by "${answers.name}".` : "",
    answers.goals ? `Their goals for the term: "${answers.goals}".` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    `[The student just finished onboarding in the mini app. ${facts} ` +
    "Open the conversation: greet them by name, reflect back two or three specifics from their profile, " +
    "then offer one concretely useful thing you can do right now. Add one line saying you also live in " +
    "their Telegram DMs — same you, same memory, they can text you anytime. " +
    "Keep it under five short sentences, warm, no hype.]"
  );
}

function Root() {
  const nav = useNav();
  const [entry, setEntry] = useState<Entry>("loading");
  const [profile, setProfile] = useState<OnboardingProfile | null>(null);

  useEffect(() => {
    void decideEntry().then((decision) => {
      setProfile(decision.profile);
      if (decision.entry === "learn") {
        // Nav state initialized off the bare URL before this resolved — align
        // the URL and section as if the visitor had opened /learn directly.
        window.history.replaceState(null, "", "/learn");
        nav.setHomeSection("learn");
        setEntry("app");
        return;
      }
      setEntry(decision.entry);
    });
  }, []);

  const handleComplete = useCallback(
    (answers: { name: string; goals: string }) => {
      // In a browser there is no bot to talk to yet — the payoff is the
      // Telegram bridge. Inside Telegram, straight into the payoff chat.
      if (!isHosted) {
        setEntry("bridge");
        return;
      }
      armPayoffKickoff(kickoffText(answers));
      setEntry("app");
      nav.push({ name: "froshbot" });
    },
    [nav],
  );

  if (entry === "loading") return null;
  if (entry === "onboarding") {
    return <OnboardingFlow profile={profile} onComplete={handleComplete} />;
  }
  if (entry === "bridge") return <WebBridge />;
  return <Chrome />;
}

/** Show the crowd only once it flatters; the personal rank works at any size. */
const SCHOOL_COUNT_FLOOR = 25;

/** Browser-onboarding landing (web only — Telegram ends in the payoff chat):
 * email capture onto the iOS waitlist, with per-school rank as the receipt.
 * No Telegram link anywhere on the web surface (decision 9 Aug 2026) — the
 * claim mechanism stays server-side, the web just stops advertising it. */
function WebBridge() {
  const [profile, setProfile] = useState<OnboardingProfile | null>(null);
  const [entry, setEntry] = useState<WaitlistEntry | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void Promise.all([fetchProfile(), fetchWaitlist()]).then(([p, w]) => {
      setProfile(p);
      setEntry(w);
      setLoaded(true);
    });
  }, []);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const join = () => {
    if (!emailOk || submitting) return;
    setSubmitting(true);
    setFailed(false);
    void joinWaitlist(email.trim()).then((result) => {
      setSubmitting(false);
      if (result) setEntry(result);
      else setFailed(true);
    });
  };

  if (!loaded) return null;
  const name = profile?.displayName ?? "";

  if (entry) {
    return (
      <div className="onb onb--hero">
        <div className="onb-hero">
          <h1 className="t-title onb-hero__title">
            You're #{entry.rank} at {entry.school}.
          </h1>
          <p className="onb-hero__sub">
            {entry.schoolCount >= SCHOOL_COUNT_FLOOR
              ? `${entry.schoolCount} students at ${entry.school} are waiting with you. I'll email you the second the app drops — everything you told me comes with you.`
              : "I'll email you the second the app drops — everything you told me comes with you."}
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => window.location.assign("/learn")}
          >
            Set up my courses →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="onb onb--hero">
      <div className="onb-hero">
        <h1 className="t-title onb-hero__title">
          You made it, that's everything{name ? `, ${name}` : ""}.
          <br />
          You're on the list!
        </h1>
        <p className="onb-hero__sub">
          Drop your email to finish creating your account — then head inside to set up your course
          schedule!
        </p>
        <input
          className="field onb-field"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@school.edu"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") join();
          }}
        />
        <button
          type="button"
          className="btn btn--primary"
          disabled={!emailOk || submitting}
          onClick={join}
        >
          {submitting ? "Saving…" : "Let me in!"}
        </button>
        {failed ? (
          <p className="t-caption onb-hint">That didn't save — give it another tap.</p>
        ) : null}
      </div>
    </div>
  );
}

function Chrome() {
  const { tab, stack, push } = useNav();
  const top = stack[stack.length - 1];

  return (
    <>
      {isHosted ? <TabRail /> : null}
      {top ? <PushedScreen route={top} /> : <TabScreen tab={tab} />}
      {/* The pill reaches FroshBot from anywhere — except from FroshBot.
       * Telegram only: the browser build has no bot surface behind it
       * (decision 9 Aug 2026), so the pill would just open the gate. */}
      {isHosted && top?.name !== "froshbot" ? (
        <FroshBotPill onOpen={() => push({ name: "froshbot" })} />
      ) : null}
      {isHosted ? null : <BottomTabBar />}
    </>
  );
}

function TabScreen({ tab }: { tab: ReturnType<typeof useNav>["tab"] }) {
  // Browser build: the rail shows the app's full shape, but only Home works —
  // every other tab is the full-app gate (decision 8 Aug 2026).
  if (!isHosted && tab !== "home") return <AppGate />;
  switch (tab) {
    case "home":
      return <HomeTab />;
    case "connect":
      return <ConnectTab />;
    case "study":
      return <StudyTab />;
    case "inbox":
      return <AppGate />;
    case "profile":
      return <ProfileTab />;
  }
}

function PushedScreen({ route }: { route: Route }) {
  // Browser build: no drilldowns. Anything pushed over the Home overview or
  // the Learn feed — post detail, calendar, search, Froshbot — is the gate.
  if (!isHosted) return <AppGate />;
  switch (route.name) {
    case "courseDetail":
      return <CourseDetail courseId={route.courseId} />;
    case "professor":
      return <ProfessorScreen professorId={route.professorId} />;
    case "postDetail":
      return <PostDetail postId={route.postId} />;
    case "matchDetail":
      return <MatchDetail userId={route.userId} />;
    case "publicProfile":
      return <PublicProfile userId={route.userId} />;
    case "search":
      return <SearchScreen />;
    case "calendar":
      return <CalendarScreen />;
    case "marketplace":
      return <MarketplaceScreen />;
    case "rewards":
      return <RewardsHistoryScreen />;
    case "settings":
      return <SettingsScreen />;
    case "editProfile":
      return <EditProfile />;
    case "horoscope":
      return <HoroscopeScreen />;
    case "froshbot":
      return <FroshBotScreen />;
    default:
      return (
        <Screen>
          <EmptyState title="Not built yet" body="This screen is next up." />
        </Screen>
      );
  }
}
